import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from "../common/SEO";
import Layout from "../common/Layout";
import { productsAPI, nodesAPI } from "../utils/api";
import ProductChildrenGrid from '../elements/product/ProductChildrenGrid';
import ProductListing from '../elements/product/ProductListing';
import './ProductNodes.scss';
import { FaSpinner } from 'react-icons/fa';

const ProductNodes = () => {
  const { productSlug, nodePath } = useParams();
  const [product, setProduct] = useState(null);
  const [productId, setProductId] = useState(null); // Internal product ID for API calls
  const [currentNode, setCurrentNode] = useState(null);
  const [children, setChildren] = useState([]);
  const [pathNodes, setPathNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, and multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  // Resolve product slug to product ID
  useEffect(() => {
    if (!productSlug) return;

    const resolveProduct = async () => {
      try {
        // First, try to parse as numeric ID (backward compatibility)
        const numericId = parseInt(productSlug, 10);
        if (!isNaN(numericId)) {
          try {
            const data = await productsAPI.getById(numericId);
            setProduct(data);
            setProductId(numericId);
            return;
          } catch (err) {
            // If numeric ID fails, continue to slug lookup
          }
        }

        // Fetch all products and find by slug
        const allProducts = await productsAPI.getAll();
        const foundProduct = allProducts.find(p => {
          const productSlugFromTitle = generateSlug(p.title);
          return productSlugFromTitle === productSlug || p.slug === productSlug;
        });

        if (foundProduct) {
          setProduct(foundProduct);
          setProductId(foundProduct.id);
        } else {
          console.warn('Product not found for slug:', productSlug);
          setError(`Product not found: ${productSlug}`);
          // Create a minimal product object for display
          setProduct({
            id: null,
            title: `Product ${productSlug}`,
          });
        }
      } catch (err) {
        console.error('Error resolving product:', err);
        setError(err.message);
      }
    };

    resolveProduct();
  }, [productSlug]);

  // Parse nodePath from URL - now using slugs instead of IDs
  let slugPathArray = [];
  if (nodePath) {
    if (Array.isArray(nodePath)) {
      slugPathArray = nodePath;
    } else {
      slugPathArray = nodePath.split('/').filter(Boolean);
    }
  }
  const currentSlugPath = slugPathArray;
  const parentNodeSlug = currentSlugPath.length > 0 ? currentSlugPath[currentSlugPath.length - 1] : null;

  // Get product slug for URLs
  const getProductSlug = () => {
    if (product?.slug) return product.slug;
    if (product?.title) return generateSlug(product.title);
    return productSlug; // Fallback to URL param
  };

  // Fetch path nodes for breadcrumbs (by slug)
  useEffect(() => {
    if (!productId || !currentSlugPath || currentSlugPath.length === 0) {
      setPathNodes([]);
      setCurrentNode(null);
      return;
    }

    const fetchPathNodes = async () => {
      try {
        // Get all nodes to match by slug or fallback to ID-based lookup
        const allNodes = await nodesAPI.getByProduct(productId);
        const nodes = [];
        
        for (const slug of currentSlugPath) {
          // First try to find by slug
          let node = allNodes.find(n => n.slug === slug);
          
          // If not found by slug, check if it's a generated slug (node-{id} format)
          if (!node && slug.startsWith('node-')) {
            const nodeId = parseInt(slug.replace('node-', ''), 10);
            if (!isNaN(nodeId)) {
              node = allNodes.find(n => n.id === nodeId);
            }
          }
          
          // If still not found, try to match by generated slug from title
          if (!node) {
            node = allNodes.find(n => {
              const generatedSlug = generateSlug(n.title);
              return generatedSlug === slug;
            });
          }
          
          if (node) {
            nodes.push(node);
          } else {
            console.warn(`Node not found for slug "${slug}"`);
            // If a node in the path fails, stop building the path
            break;
          }
        }
        
        setPathNodes(nodes);
        
        // Set current node
        if (nodes.length > 0) {
          setCurrentNode(nodes[nodes.length - 1]);
        }
      } catch (err) {
        console.error('Error fetching path nodes:', err);
      }
    };
    fetchPathNodes();
  // Intended: run when product or path changes. currentSlugPath derived from nodePath (stable string dep).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, currentSlugPath.join('/')]);

  // Fetch children
  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchChildren = async () => {
      try {
        let childrenData;
        if (currentSlugPath.length === 0) {
          // Fetch root children
          childrenData = await nodesAPI.getByProduct(productId, 1);
        } else {
          // Fetch children of current node (by slug)

          // Get all nodes to find the parent node
          const allNodes = await nodesAPI.getByProduct(productId);
          
          // Try to find by slug first
          let parentNode = allNodes.find(n => n.slug === parentNodeSlug);
          
          // If not found by slug, check if it's a generated slug (node-{id} format)
          if (!parentNode && parentNodeSlug.startsWith('node-')) {
            const nodeId = parseInt(parentNodeSlug.replace('node-', ''), 10);
            if (!isNaN(nodeId)) {
              parentNode = allNodes.find(n => n.id === nodeId);
            }
          }
          
          // If still not found, try to match by generated slug from title
          if (!parentNode) {
            parentNode = allNodes.find(n => {
              const generatedSlug = generateSlug(n.title);
              return generatedSlug === parentNodeSlug;
            });
          }
          
          if (parentNode) {
            childrenData = await nodesAPI.getChildren(parseInt(productId, 10), parentNode.id);
          } else {
            console.error('Parent node not found for slug:', parentNodeSlug);
            childrenData = [];
          }
        }
        
        // Ensure childrenData is an array
        if (!Array.isArray(childrenData)) {
          console.warn('Children data is not an array:', childrenData);
          setChildren([]);
        } else {
          setChildren(childrenData);
        }
      } catch (err) {
        console.error('Error fetching children:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          productId,
          currentSlugPath,
          parentNodeSlug
        });
        setError(err.message);
        setChildren([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchChildren();
  // Intended: run when product or path changes. currentSlugPath/parentNodeSlug from nodePath.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, parentNodeSlug, currentSlugPath.join('/')]);

  const buildBreadcrumbPath = (index) => {
    const slug = getProductSlug();
    if (index === -1) return `/product-nodes/${slug}`;
    const path = currentSlugPath.slice(0, index + 1);
    return `/product-nodes/${slug}/${path.join('/')}`;
  };

  // Helper to normalize path (remove double slashes)
  const normalizePath = (base, path) => {
    const baseUrl = (base || '').replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  const handleNodeClick = async (nodeId) => {
    // Get the node to get its slug
    try {
      if (!productId) return;
      const allNodes = await nodesAPI.getByProduct(productId);
      const node = allNodes.find(n => n.id === nodeId);
      const productSlugForUrl = getProductSlug();
      if (node) {
        // Generate slug from title if slug is missing (backward compatibility)
        const slug = node.slug || generateSlug(node.title) || `node-${nodeId}`;
        const newPath = [...currentSlugPath, slug];
        window.location.href = normalizePath(process.env.PUBLIC_URL, `/product-nodes/${productSlugForUrl}/${newPath.join('/')}`);
      } else {
        console.error('Node not found:', nodeId);
        // Fallback: use ID if node not found (shouldn't happen, but handle gracefully)
        const newPath = [...currentSlugPath, `node-${nodeId}`];
        window.location.href = normalizePath(process.env.PUBLIC_URL, `/product-nodes/${productSlugForUrl}/${newPath.join('/')}`);
      }
    } catch (err) {
      console.error('Error fetching node for navigation:', err);
    }
  };

  const handleDetailClick = async (nodeId) => {
    try {
      if (!productId) return;
      const pathSlugs = currentSlugPath.join(',');
      window.location.href = normalizePath(process.env.PUBLIC_URL, `/product-details/${productId}?nodeId=${nodeId}&path=${pathSlugs}`);
    } catch (err) {
      console.error('Error fetching node for detail navigation:', err);
    }
  };

  // Show loading only if we're still fetching children
  // Don't wait for product to load
  if (loading && children.length === 0) {
    return (
      <Layout>
        <SEO title="Loading..." />
        <div className="product-nodes-loading">
          <FaSpinner className="spinner" />
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  // Always render the page - don't block on errors
  // Show error message but still allow navigation
  // Only show full error page if we can't fetch children AND there's a critical error
  const hasCriticalError = error && children.length === 0 && !loading && error.includes('Failed to fetch');
  
  if (hasCriticalError) {
    return (
      <Layout>
        <SEO title="Error" />
        <div className="product-nodes-error">
          <p>Error: {error}</p>
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
            Please ensure the backend server is running and the API is accessible.
          </p>
          <Link to={normalizePath(process.env.PUBLIC_URL, '/products')} style={{ marginTop: '20px', display: 'inline-block' }}>Back to Products</Link>
        </div>
      </Layout>
    );
  }

  // Use product title if available, otherwise use currentNode or fallback
  const pageTitle = currentNode 
    ? currentNode.title 
    : (product?.title || `Product ${productSlug}`);
  // Check if we're at a product listing level (all children are detail nodes)
  const isProductListing = children.length > 0 && children.every(c => c.is_detail === true);

  return (
    <>
      <SEO title={`${pageTitle} - KR Power`} />
      <Layout>
        <div className="product-nodes-page">
          {/* Breadcrumbs */}
          <nav className="product-breadcrumbs" aria-label="Breadcrumb">
            <ol className="breadcrumb-list">
              <li className="breadcrumb-item">
                <Link to={normalizePath(process.env.PUBLIC_URL, '/products')}>KR Power</Link>
              </li>
              <li className="breadcrumb-item">
                <span>/</span>
                <Link to={normalizePath(process.env.PUBLIC_URL, '/products')}>Products</Link>
              </li>
              <li className="breadcrumb-item">
                <span>/</span>
                <Link to={buildBreadcrumbPath(-1)}>{product?.title || `Product ${productSlug}`}</Link>
              </li>
              {pathNodes.map((node, index) => (
                <li key={node.id} className="breadcrumb-item">
                  <span>/</span>
                  <Link to={buildBreadcrumbPath(index)}>{node.title}</Link>
                </li>
              ))}
            </ol>
          </nav>

          {/* Page Header */}
          <header className="product-nodes-header">
            <h1 className="product-nodes-title">{pageTitle}</h1>
            {children.length > 0 && (
              <div className="product-nodes-controls">
                <div className="view-toggle">
                  <button 
                    className={viewMode === 'grid' ? 'active' : ''}
                    onClick={() => setViewMode('grid')}
                    aria-label="Grid view"
                  >
                    <span>☰</span>
                  </button>
                  <button 
                    className={viewMode === 'list' ? 'active' : ''}
                    onClick={() => setViewMode('list')}
                    aria-label="List view"
                  >
                    <span>☷</span>
                  </button>
                </div>
                {isProductListing && (
                  <div className="product-count">
                    {children.length} Article{children.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </header>

          {/* Content */}
          {loading ? (
            <div className="product-nodes-loading">
              <FaSpinner className="spinner" />
              <p>Loading children...</p>
            </div>
          ) : error ? (
            <div className="product-nodes-error">
              <p>Error: {error}</p>
            </div>
          ) : children.length === 0 ? (
            <div className="product-nodes-empty">
              <p>No categories or products found for this product.</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                Please create categories/subcategories in the admin panel.
              </p>
            </div>
          ) : isProductListing ? (
            <ProductListing 
              products={children}
              productId={productId}
              currentPath={currentSlugPath}
              onProductClick={handleDetailClick}
            />
          ) : (
            <ProductChildrenGrid
              children={children}
              productId={productId}
              productSlug={getProductSlug()}
              currentPath={currentSlugPath}
              onNodeClick={handleNodeClick}
              onDetailClick={handleDetailClick}
              viewMode={viewMode}
            />
          )}
        </div>
      </Layout>
    </>
  );
};

export default ProductNodes;

